import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { VIEW_MODE_GRID } from '../constants/Playbook'
import type { EditorTab } from '../Types'
import { EDITOR_TAB_OPTIONS } from '../Types'

export type ViewMode = 'grid' | 'list'
export type { EditorTab }

interface PlaybookEditorState {
	// View state
	viewMode: ViewMode
	searchQuery: string
	activeTab: EditorTab

	// Trash view
	isTrashView: boolean

	// New item state (for dialogs)
	newItemName: string

	// Rename state
	renamePlayId: string | null
	renamePlayName: string

	// Play viewer state
	viewingPlayId: string | null
}

interface PlaybookEditorStateActions {
	setViewMode: (mode: ViewMode) => void
	setSearchQuery: (query: string) => void
	setActiveTab: (tab: EditorTab) => void
	setIsTrashView: (isTrash: boolean) => void
	setNewItemName: (name: string) => void
	setRenamePlayId: (id: string | null) => void
	setRenamePlayName: (name: string) => void
	setViewingPlayId: (id: string | null) => void
	resetNewItemState: () => void
	resetRenameState: () => void
}

interface UsePlaybookEditorStateReturn {
	state: PlaybookEditorState
	actions: PlaybookEditorStateActions
}

/**
 * Hook for managing PlaybookEditor UI state
 *
 * Consolidates all local UI state into a single hook for better
 * maintainability and reduced complexity in the main component.
 */
export function usePlaybookEditorState(): UsePlaybookEditorStateReturn {
	const [searchParams, setSearchParams] = useSearchParams()

	// View mode with localStorage persistence
	const [viewMode, setViewModeInternal] = useState<ViewMode>(() => {
		const cached = localStorage.getItem('playbook-view-mode')
		return (cached === 'grid' || cached === 'list') ? cached : VIEW_MODE_GRID
	})

	const [searchQuery, setSearchQuery] = useState('')

	// Initialize active tab from URL ?tab= param, defaulting to 'plays'
	const validTabs = EDITOR_TAB_OPTIONS.map(o => o.value) as string[]
	const tabParam = searchParams.get('tab')
	const [activeTab, setActiveTabInternal] = useState<EditorTab>(
		tabParam && validTabs.includes(tabParam) ? tabParam as EditorTab : 'plays'
	)

	// Clear the ?tab= param after reading it so it doesn't persist in the URL
	useEffect(() => {
		if (searchParams.has('tab')) {
			searchParams.delete('tab')
			setSearchParams(searchParams, { replace: true })
		}
		// Only run on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const setActiveTab = useCallback((tab: EditorTab) => {
		setActiveTabInternal(tab)
	}, [])

	// Trash view
	const [isTrashView, setIsTrashView] = useState(false)

	// New item state
	const [newItemName, setNewItemName] = useState('')

	// Rename state
	const [renamePlayId, setRenamePlayId] = useState<string | null>(null)
	const [renamePlayName, setRenamePlayName] = useState('')

	// Play viewer state
	const [viewingPlayId, setViewingPlayId] = useState<string | null>(null)

	// Persist view mode to localStorage
	useEffect(() => {
		localStorage.setItem('playbook-view-mode', viewMode)
	}, [viewMode])

	// Wrapped setViewMode that also persists
	const setViewMode = useCallback((mode: ViewMode) => {
		setViewModeInternal(mode)
	}, [])

	// Reset functions for cleaner state management
	const resetNewItemState = useCallback(() => {
		setNewItemName('')
	}, [])

	const resetRenameState = useCallback(() => {
		setRenamePlayId(null)
		setRenamePlayName('')
	}, [])

	const state: PlaybookEditorState = {
		viewMode,
		searchQuery,
		activeTab,
		isTrashView,
		newItemName,
		renamePlayId,
		renamePlayName,
		viewingPlayId,
	}

	const actions: PlaybookEditorStateActions = {
		setViewMode,
		setSearchQuery,
		setActiveTab,
		setIsTrashView,
		setNewItemName,
		setRenamePlayId,
		setRenamePlayName,
		setViewingPlayId,
		resetNewItemState,
		resetRenameState,
	}

	return { state, actions }
}
